import React from 'react';
import {FieldArray, FieldArrayRenderProps, Form, Formik, FormikHelpers} from "formik";
import FormControls from "../../../components/FormControls";
import {Attribute, AttributeData, AttributeType} from "../../../types/Attribute";
import {NumberField, SelectField, TextField} from "../../../components/Form/Form";
import Button from "../../../components/Button";
import {PlusSmIcon, TrashIcon} from "@heroicons/react/solid";
import IconName from "../../../types/IconName";

// Types ----

type AttributeFormProps = {
  attribute?: Attribute | null
  onSubmit: (values: AttributeData, helpers: FormikHelpers<AttributeData>) => any
  onCancel?: (helpers?: FormikHelpers<AttributeData>) => any
}

// Helpers ----

const attributeTypeLabels: Record<AttributeType, string> = {
  [AttributeType.BOOLEAN]: 'Yes/No',
  [AttributeType.DATE]: 'Date',
  [AttributeType.SELECT]: 'Select',
  [AttributeType.STRING]: 'Text',
}

const attributeTypeIcons: Record<AttributeType, IconName> = {
  [AttributeType.BOOLEAN]: IconName.CHECK_CIRCLE,
  [AttributeType.DATE]: IconName.CALENDAR,
  [AttributeType.SELECT]: IconName.SELECTOR,
  [AttributeType.STRING]: IconName.PENCIL,
}

const attributeTypeDescriptions: Record<AttributeType, string> = {
  [AttributeType.BOOLEAN]: 'A yes/no choice',
  [AttributeType.DATE]: 'Choose a date',
  [AttributeType.SELECT]: 'Choose from a list of options',
  [AttributeType.STRING]: 'Enter a some text',
}

// TODO: Remove array filter when date attribute can safely be used (NGAGE-2778)
const attributeTypeOptions = Object.values(AttributeType).map(type => ({
  type: 'attribute-type',
  id: type,
  name: attributeTypeLabels[type],
  icon: attributeTypeIcons[type],
  description: attributeTypeDescriptions[type],
})).filter(attribute => attribute.id !== AttributeType.DATE);

// Component ----

function AttributeForm({attribute, onSubmit, onCancel}: AttributeFormProps) {

  const initialValues: AttributeData = {
    type: attribute?.type ?? AttributeType.STRING,
    name: attribute?.name ?? '',
    label: attribute?.label ?? '',
    rules: attribute?.rules ?? {},
  }

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {formik => (
        <Form className="space-y-8 divide-y divide-gray-200">
          <div className="shadow sm:rounded-md">
            <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
              <div className="space-y-8 divide-y divide-gray-200">
                <div>
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      New Attribute
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Add a new attribute that can be used to record extra data
                    </p>
                  </div>
                  <div className="mt-6 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6">
                    <div className="sm:col-span-6">
                      <SelectField
                        name="type"
                        label="Type"
                        hint="required"
                        help="This cannot be changed once saved"
                        options={attributeTypeOptions}
                        disabled={!!attribute}
                        required
                        onChange={() => formik.setFieldValue('rules', {})}
                      />
                    </div>

                    <div className="sm:col-span-6">
                      <TextField
                        name="name"
                        label="Name"
                        hint="required"
                        help="A short internal name"
                        required
                      />
                    </div>

                    <div className="sm:col-span-6">
                      <TextField
                        name="label"
                        label="Label"
                        hint="required"
                        help="This is shown against the attribute in the audience app"
                        required
                      />
                    </div>

                    {/* Date Rules */}
                    {/*
                      {formik.values.type === AttributeType.DATE &&
                        <>
                          <div className="sm:col-span-3">
                            <TextField
                              name="rules.min"
                              label="Earliest Date"
                              hint="optional"
                              help="This can be a date or relative expression (e.g. '-100 years')"
                            />
                          </div>

                          <div className="sm:col-span-3">
                            <TextField
                              name="rules.max"
                              label="Latest Date"
                              hint="optional"
                              help="This can be a date or relative expression (e.g. 'now')"
                            />
                          </div>
                        </>
                      }
                    */}

                    {/* Select Rules */}
                    {formik.values.type === AttributeType.SELECT &&
                      <fieldset className="sm:col-span-6">
                        <legend className="block text-sm font-medium leading-6 text-gray-900">
                          Options
                        </legend>

                        <div className="mt-2">
                          <FieldArray name="rules.options">
                            {({form, push, remove}: FieldArrayRenderProps) => (
                              <div>
                                <ul>
                                  {form.values.rules.options?.map((_option: string, index: number) => (
                                    <li key={index} className="w-full flex space-x-2 my-1">
                                      <div className="w-full">
                                        <TextField
                                          name={`rules.options.${index}`}
                                          required
                                        />
                                      </div>
                                      <Button
                                        color={Button.colors.WHITE}
                                        size={Button.sizes.TINY}
                                        icon={TrashIcon}
                                        onClick={() => remove(index)}
                                      />
                                    </li>
                                  ))}
                                </ul>

                                <div className="mt-1">
                                  <Button
                                    color={Button.colors.WHITE}
                                    size={Button.sizes.TINY}
                                    icon={PlusSmIcon}
                                    onClick={() => push('')}
                                  >
                                    Add Option
                                  </Button>
                                </div>
                              </div>
                            )}
                          </FieldArray>
                        </div>
                      </fieldset>
                    }

                    {/* String Rules */}
                    {formik.values.type === AttributeType.STRING &&
                      <>
                        <div className="sm:col-span-3">
                          <NumberField
                            name="rules.min"
                            label="Minimum Length"
                            hint="optional"
                            help="A positive whole number"
                            min={0}
                            max={1000}
                          />
                        </div>

                        <div className="sm:col-span-3">
                          <NumberField
                            name="rules.max"
                            label="Maximum Length"
                            hint="optional"
                            help="A positive whole number"
                            min={1}
                            max={1000}
                          />
                        </div>
                      </>
                    }
                  </div>
                </div>
              </div>
            </div>

            <FormControls formik={formik} onCancel={onCancel}/>
          </div>
        </Form>
      )}
    </Formik>
  );
}

// Export ----

export default AttributeForm;